import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { setPwaUpdateState } from '../../services/pwaUpdateService';
import { setFcmServiceWorkerRegistration } from '../../services/pushService';
import { nuclearReset } from '../../services/appResetService';
import { logDebug, logError } from '../../services/loggerService';

// How often to actively poll the SW script for changes, on top of the
// browser's own navigation-time checks. Catches deploys for long-lived
// open sessions where the user doesn't reload or switch tabs.
const SW_UPDATE_INTERVAL_MS = 15 * 60 * 1000;

// How long to wait for the natural SW skip-waiting + controllerchange +
// reload to complete before falling back to nuclearReset. On Chrome /
// desktop / Android this whole flow finishes in ~500ms-1s. On iOS
// installed PWA, controllerchange is unreliable and updateServiceWorker(true)
// can hang indefinitely — without this fallback the user is stuck staring
// at "Updating..." with no recovery. 5s is comfortably above the happy-path
// latency and short enough that the user doesn't think it's broken.
const UPDATE_FALLBACK_DELAY_MS = 5000;

/**
 * Renderless component that owns the `useRegisterSW()` hook from
 * vite-plugin-pwa and feeds its state into pwaUpdateService.
 *
 * Why a dedicated component (vs. calling the hook directly in App.tsx
 * or in UpdateAvailableBanner): the hook registers the SW exactly once
 * per mount. If the banner unmounted and remounted we'd double-register
 * or fight Workbox's lifecycle. By mounting this once at the App root,
 * registration is stable for the app's lifetime and the banner is just
 * a passive subscriber.
 *
 * Also handles a side-channel: when the PWA SW registers, the FCM SW
 * registration we captured in main.tsx may need to be refreshed if the
 * SW lifecycle reset both registrations. We re-capture defensively here.
 */
export const PwaUpdateInitializer: React.FC = () => {
  // Stash the SW registration in a ref so the polling + focus/visibility
  // effects below can call registration.update() without re-running
  // whenever it appears. The registration is delivered async via the
  // onRegisteredSW callback; the effects mount immediately and read
  // through the ref once it's populated.
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      registrationRef.current = registration ?? null;
      logDebug('PWA SW registered', undefined, { swUrl, scope: registration?.scope });
    },
    onRegisterError(error) {
      logError(error instanceof Error ? error : new Error(String(error)), 'PWA SW registration failed');
    },
    onNeedRefresh() {
      logDebug('PWA SW: new version available, banner will appear');
    },
  });

  // Active update polling. The browser's built-in SW update check fires
  // only on navigation requests, which don't happen for long-lived single-
  // page sessions that stay on one route. Without these, a user who opens
  // the PWA in the morning and leaves it open all day would never see the
  // banner — they'd find out about new versions only when they reload.
  //
  // Two triggers, both cheap (registration.update() is a HEAD-ish fetch
  // of the SW script; idempotent and no-ops if the script hasn't changed):
  //   - 15-minute interval: covers users who keep the PWA open without
  //     ever blurring/focusing (rare on mobile, common on desktop).
  //   - focus + visibilitychange: covers users who switch away to another
  //     app/tab and come back. iOS Safari's PWA lifecycle in particular
  //     relies on visibilitychange because focus events aren't always
  //     dispatched when the PWA is brought back from background.
  useEffect(() => {
    const checkForUpdate = () => {
      const reg = registrationRef.current;
      if (!reg) return;
      reg.update().catch((err) => {
        logDebug('PWA SW: update check failed (non-fatal)', undefined, {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    };

    const intervalId = window.setInterval(checkForUpdate, SW_UPDATE_INTERVAL_MS);

    const onFocus = () => checkForUpdate();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // Whenever needRefresh changes, push to the service singleton so the
    // banner re-renders. The updateAndReload binding here closes over the
    // current updateServiceWorker reference; vite-plugin-pwa's hook keeps
    // it stable across renders, but capturing on every effect run is the
    // safe move.
    setPwaUpdateState({
      needRefresh,
      updateAndReload: async () => {
        logDebug('PWA SW: user requested update, calling updateServiceWorker(true)');

        // Belt-and-suspenders for iOS installed PWA. updateServiceWorker(true)
        // is the right path on Chrome/Android/desktop: SKIP_WAITING →
        // controllerchange → reload. But iOS PWA frequently fails to fire
        // controllerchange after skipWaiting, which means the helper hangs
        // and the page never reloads — banner stuck on "Updating..." with
        // no recovery.
        //
        // Schedule a nuclearReset as a deferred fallback. If the normal
        // flow reloads within UPDATE_FALLBACK_DELAY_MS, the page is gone
        // and this timeout never fires. If the iOS stuck state has us,
        // the timeout fires and forces a hard reset (unregister all SWs,
        // wipe CacheStorage, cache-busted reload) — guaranteed to land
        // the user on fresh code.
        const fallbackId = window.setTimeout(() => {
          logDebug('PWA SW: normal update did not reload in time, nuclearReset fallback firing');
          void nuclearReset();
        }, UPDATE_FALLBACK_DELAY_MS);

        try {
          await updateServiceWorker(true);
          // If we get here without the page having reloaded, something is
          // wrong but not necessarily timeout-worthy yet. The setTimeout
          // above will catch it. We do NOT clearTimeout(fallbackId) here —
          // if updateServiceWorker resolved without reloading, we still
          // need the fallback to fire.
        } catch (err) {
          window.clearTimeout(fallbackId);
          logError(
            err instanceof Error ? err : new Error(String(err)),
            'updateServiceWorker(true) threw — falling back to nuclearReset',
          );
          await nuclearReset();
        }
      },
    });
  }, [needRefresh, updateServiceWorker]);

  // Re-capture the FCM SW registration on mount. The PWA SW's installation
  // doesn't displace the FCM SW (disjoint scope), but if main.tsx's capture
  // somehow raced or got cleared, this picks it back up so getPushToken()
  // can pass it explicitly to FirebaseMessaging.getToken().
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => {
        const fcmReg = regs.find((r) => r.scope.endsWith('/firebase-cloud-messaging-push-scope/'));
        if (fcmReg) setFcmServiceWorkerRegistration(fcmReg);
      })
      .catch(() => {
        /* non-fatal */
      });
  }, []);

  return null;
};
